import Box from "@mui/material/Box";
import { useMemo } from "react";
import { Controller, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import IntakeTextField from "../../../components/Form/IntakeTextField";
import {
  ENTERPRISE_CREATIVES_VARY_BY_MARKET_OPTIONS,
  ENTERPRISE_CURRENT_TOOL_OR_ADLE_STATUS_OPTIONS,
  ENTERPRISE_PURCHASE_DECISION_OPTIONS,
} from "../../../utils/constants";
import {
  ENTERPRISE_INTAKE_FIELD_COMPANY_NAME,
  ENTERPRISE_INTAKE_FIELD_COMPANY_WEBSITE,
  ENTERPRISE_INTAKE_FIELD_CORPORATE_EMAIL,
  ENTERPRISE_INTAKE_FIELD_CREATIVES_VARY_BY_MARKET,
  ENTERPRISE_INTAKE_FIELD_CURRENT_TOOL_OR_ADLE_STATUS,
  ENTERPRISE_INTAKE_FIELD_FULL_NAME,
  ENTERPRISE_INTAKE_FIELD_JOB_TITLE,
  ENTERPRISE_INTAKE_FIELD_OPERATING_COUNTRIES_COUNT,
  ENTERPRISE_INTAKE_FIELD_PURCHASE_DECISION_MAKER,
  ENTERPRISE_INTAKE_OTHER_PLACEHOLDER,
  ENTERPRISE_INTAKE_PLACEHOLDER_COMPANY_NAME,
  ENTERPRISE_INTAKE_PLACEHOLDER_COMPANY_WEBSITE,
  ENTERPRISE_INTAKE_PLACEHOLDER_CORPORATE_EMAIL,
  ENTERPRISE_INTAKE_PLACEHOLDER_FULL_NAME,
  ENTERPRISE_INTAKE_PLACEHOLDER_JOB_TITLE,
  ENTERPRISE_INTAKE_PLACEHOLDER_OPERATING_COUNTRIES_COUNT,
} from "../../../i18n/keysTranslations";
import FieldBlock from "../../PreActivation/components/FieldBlock";
import FieldError from "../../PreActivation/components/FieldError";
import FieldLabel from "../../PreActivation/components/FieldLabel";
import RadioGroup from "../../PreActivation/components/RadioGroup";
import ReadOnlySectionGrid from "../../PreActivation/components/ReadOnlySectionGrid";

const RadioWithOther = ({
  name,
  otherName,
  labelKey,
  required,
  options,
  control,
  errors,
  readOnly,
}) => {
  const { t } = useTranslation();
  const value = useWatch({ control, name });

  return (
    <FieldBlock>
      <FieldLabel htmlFor={name} text={t(labelKey)} required={required} />
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState }) => (
          <>
            <RadioGroup
              name={name}
              options={options}
              selectedValue={field.value}
              onChange={field.onChange}
              readOnly={readOnly}
            />
            <FieldError message={fieldState.error?.message} />
          </>
        )}
      />
      {value === "other" && (
        <Box sx={{ mt: 1 }}>
          <IntakeTextField
            name={otherName}
            control={control}
            rules={{}}
            errors={errors}
            fullWidth
            readOnly={readOnly}
            placeholder={t(ENTERPRISE_INTAKE_OTHER_PLACEHOLDER)}
            variant="outlined"
          />
        </Box>
      )}
    </FieldBlock>
  );
};

const parseOperatingCountriesCount = (value) => {
  const num = parseInt(value, 10);
  return isNaN(num) ? 0 : num;
};

const ContactSection = ({ control, errors, readOnly }) => {
  const { t } = useTranslation();

  const purchaseDecisionOptions = useMemo(
    () =>
      ENTERPRISE_PURCHASE_DECISION_OPTIONS.map((o) => ({
        value: o.value,
        label: o.labelKey ? t(o.labelKey) : o.label,
      })),
    [t]
  );

  const currentToolOrAdleStatusOptions = useMemo(
    () =>
      ENTERPRISE_CURRENT_TOOL_OR_ADLE_STATUS_OPTIONS.map((o) => ({
        value: o.value,
        label: o.labelKey ? t(o.labelKey) : o.label,
      })),
    [t]
  );

  const creativesVaryByMarketOptions = useMemo(
    () =>
      ENTERPRISE_CREATIVES_VARY_BY_MARKET_OPTIONS.map((o) => ({
        value: o.value,
        label: o.labelKey ? t(o.labelKey) : o.label,
      })),
    [t]
  );

  const operatingCountriesCount = useWatch({
    control,
    name: "operatingCountriesCount",
  });
  const creativesVaryByMarket = useWatch({
    control,
    name: "creativesVaryByMarket",
  });
  const countriesCount = parseOperatingCountriesCount(operatingCountriesCount);
  const showCreativesVaryByMarket = readOnly
    ? Boolean(creativesVaryByMarket)
    : countriesCount > 1;

  return (
    <ReadOnlySectionGrid readOnly={readOnly}>
      <FieldBlock>
        <FieldLabel
          htmlFor="fullName"
          text={t(ENTERPRISE_INTAKE_FIELD_FULL_NAME)}
          required
        />
        <IntakeTextField
          name="fullName"
          control={control}
          rules={{}}
          errors={errors}
          fullWidth
          readOnly={readOnly}
          placeholder={t(ENTERPRISE_INTAKE_PLACEHOLDER_FULL_NAME)}
          variant="outlined"
        />
      </FieldBlock>

      <FieldBlock>
        <FieldLabel
          htmlFor="corporateEmail"
          text={t(ENTERPRISE_INTAKE_FIELD_CORPORATE_EMAIL)}
          required
        />
        <IntakeTextField
          name="corporateEmail"
          control={control}
          rules={{}}
          errors={errors}
          fullWidth
          readOnly={readOnly}
          placeholder={t(ENTERPRISE_INTAKE_PLACEHOLDER_CORPORATE_EMAIL)}
          variant="outlined"
        />
      </FieldBlock>

      <FieldBlock>
        <FieldLabel
          htmlFor="companyName"
          text={t(ENTERPRISE_INTAKE_FIELD_COMPANY_NAME)}
          required
        />
        <IntakeTextField
          name="companyName"
          control={control}
          rules={{}}
          errors={errors}
          fullWidth
          readOnly={readOnly}
          placeholder={t(ENTERPRISE_INTAKE_PLACEHOLDER_COMPANY_NAME)}
          variant="outlined"
        />
      </FieldBlock>

      <FieldBlock>
        <FieldLabel
          htmlFor="jobTitle"
          text={t(ENTERPRISE_INTAKE_FIELD_JOB_TITLE)}
          required
        />
        <IntakeTextField
          name="jobTitle"
          control={control}
          rules={{}}
          errors={errors}
          fullWidth
          readOnly={readOnly}
          placeholder={t(ENTERPRISE_INTAKE_PLACEHOLDER_JOB_TITLE)}
          variant="outlined"
        />
      </FieldBlock>

      <FieldBlock>
        <FieldLabel
          htmlFor="companyWebsite"
          text={t(ENTERPRISE_INTAKE_FIELD_COMPANY_WEBSITE)}
        />
        <IntakeTextField
          name="companyWebsite"
          control={control}
          rules={{}}
          errors={errors}
          fullWidth
          readOnly={readOnly}
          placeholder={t(ENTERPRISE_INTAKE_PLACEHOLDER_COMPANY_WEBSITE)}
          variant="outlined"
        />
      </FieldBlock>

      <FieldBlock>
        <FieldLabel
          htmlFor="purchaseDecisionMaker"
          text={t(ENTERPRISE_INTAKE_FIELD_PURCHASE_DECISION_MAKER)}
          required
        />
        <Controller
          name="purchaseDecisionMaker"
          control={control}
          render={({ field, fieldState }) => (
            <>
              <RadioGroup
                name="purchaseDecisionMaker"
                options={purchaseDecisionOptions}
                selectedValue={field.value}
                onChange={field.onChange}
                readOnly={readOnly}
              />
              <FieldError message={fieldState.error?.message} />
            </>
          )}
        />
      </FieldBlock>

      <RadioWithOther
        name="currentToolOrAdleStatus"
        otherName="currentToolOrAdleStatusOther"
        labelKey={ENTERPRISE_INTAKE_FIELD_CURRENT_TOOL_OR_ADLE_STATUS}
        required
        options={currentToolOrAdleStatusOptions}
        control={control}
        errors={errors}
        readOnly={readOnly}
      />

      <FieldBlock>
        <FieldLabel
          htmlFor="operatingCountriesCount"
          text={t(ENTERPRISE_INTAKE_FIELD_OPERATING_COUNTRIES_COUNT)}
          required
        />
        <IntakeTextField
          name="operatingCountriesCount"
          control={control}
          rules={{}}
          errors={errors}
          fullWidth
          readOnly={readOnly}
          type="number"
          inputProps={{ min: 1, step: 1 }}
          placeholder={t(
            ENTERPRISE_INTAKE_PLACEHOLDER_OPERATING_COUNTRIES_COUNT
          )}
          variant="outlined"
        />
      </FieldBlock>

      {showCreativesVaryByMarket ? (
        <FieldBlock>
          <FieldLabel
            htmlFor="creativesVaryByMarket"
            text={t(ENTERPRISE_INTAKE_FIELD_CREATIVES_VARY_BY_MARKET)}
            required
          />
          <Controller
            name="creativesVaryByMarket"
            control={control}
            render={({ field, fieldState }) => (
              <>
                <RadioGroup
                  name="creativesVaryByMarket"
                  options={creativesVaryByMarketOptions}
                  selectedValue={field.value}
                  onChange={field.onChange}
                  readOnly={readOnly}
                />
                <FieldError message={fieldState.error?.message} />
              </>
            )}
          />
        </FieldBlock>
      ) : null}
    </ReadOnlySectionGrid>
  );
};

export default ContactSection;
